/**
 * Sound and Vibration Service
 *
 * Centralized service for managing sound effects and haptic feedback.
 * Uses Capacitor Haptics for native vibration and Web Audio API for sounds.
 *
 * Vibration patterns:
 * - Ronda: double soft vibration (two short soft impacts)
 * - Caída: strong short vibration (single heavy impact)
 * - Rondín: triple soft vibration (three short soft impacts)
 * - Pasar en Báciga: medium short vibration (single medium impact)
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { debugLog, debugError } from '../utils/debugUtils';

// Import sound file
import golpeSound from '../assets/sounds/golpe.mp3';

// Timing configuration from environment variables (with defaults)
const RONDA_PAUSE = parseInt(process.env.REACT_APP_VIBRATION_RONDA_PAUSE) || 500;
const RONDIN_PAUSE = parseInt(process.env.REACT_APP_VIBRATION_RONDIN_PAUSE) || 500;

class SoundVibrationService {
    constructor() {
        this.initialized = false;
        this.platform = Capacitor.getPlatform();
        this.soundEnabled = true;
        this.vibrationEnabled = true;
        this.audioContext = null;
        this.audioBuffer = null;
        this.audioElement = null;
    }

    /**
     * Initialize the service
     * Loads settings from localStorage and prepares audio
     */
    async initialize() {
        if (this.initialized) return;

        // Load settings from localStorage
        const savedSoundEnabled = localStorage.getItem('soundEnabled');
        const savedVibrationEnabled = localStorage.getItem('vibrationEnabled');

        this.soundEnabled = savedSoundEnabled === null ? true : savedSoundEnabled === 'true';
        this.vibrationEnabled = savedVibrationEnabled === null ? true : savedVibrationEnabled === 'true';

        // Initialize audio
        await this.initializeAudio();

        this.initialized = true;
        debugLog('[SoundVibrationService] Initialized:', {
            platform: this.platform,
            soundEnabled: this.soundEnabled,
            vibrationEnabled: this.vibrationEnabled
        });
    }

    /**
     * Initialize audio system
     * Uses HTML5 Audio element for better compatibility
     */
    async initializeAudio() {
        try {
            // Create audio element for playing sounds
            this.audioElement = new Audio(golpeSound);
            this.audioElement.preload = 'auto';

            // Try to use Web Audio API for better control
            if (window.AudioContext || window.webkitAudioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

                // Load and decode the audio file
                const response = await fetch(golpeSound);
                const arrayBuffer = await response.arrayBuffer();
                this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

                debugLog('[SoundVibrationService] Web Audio API initialized');
            }
        } catch (error) {
            debugError('[SoundVibrationService] Error initializing audio:', error);
            // Fallback to HTML5 Audio
            this.audioContext = null;
            this.audioBuffer = null;
        }
    }

    /**
     * Play the golpe (hit) sound
     */
    async playSound() {
        if (!this.soundEnabled) return;

        try {
            if (this.audioContext && this.audioBuffer) {
                // Resume context if suspended (required by browsers after user interaction)
                if (this.audioContext.state === 'suspended') {
                    await this.audioContext.resume();
                }

                // Create a new buffer source for each play (they can only be used once)
                const source = this.audioContext.createBufferSource();
                source.buffer = this.audioBuffer;
                source.connect(this.audioContext.destination);
                source.start(0);
            } else if (this.audioElement) {
                // Fallback to HTML5 Audio
                this.audioElement.currentTime = 0;
                await this.audioElement.play();
            }
        } catch (error) {
            debugError('[SoundVibrationService] Error playing sound:', error);
        }
    }

    /**
     * Trigger vibration using Capacitor Haptics
     * Falls back to Web Vibration API on unsupported platforms
     * @param {string} style - 'light', 'medium', or 'heavy'
     */
    async vibrate(style = 'medium') {
        if (!this.vibrationEnabled) return;

        try {
            if (Capacitor.isNativePlatform()) {
                // Use Capacitor Haptics for native platforms
                let impactStyle;
                switch (style) {
                    case 'light':
                        impactStyle = ImpactStyle.Light;
                        break;
                    case 'heavy':
                        impactStyle = ImpactStyle.Heavy;
                        break;
                    case 'medium':
                    default:
                        impactStyle = ImpactStyle.Medium;
                        break;
                }
                await Haptics.impact({ style: impactStyle });
            } else if (navigator.vibrate) {
                // Fallback to Web Vibration API
                let duration;
                switch (style) {
                    case 'light':
                        duration = 30;
                        break;
                    case 'heavy':
                        duration = 100;
                        break;
                    case 'medium':
                    default:
                        duration = 50;
                        break;
                }
                navigator.vibrate(duration);
            }
        } catch (error) {
            debugError('[SoundVibrationService] Error triggering vibration:', error);
        }
    }

    /**
     * Play sound and vibrate together (simulating a hit on the table)
     * @param {string} vibrationStyle - 'light', 'medium', or 'heavy'
     */
    async playHit(vibrationStyle = 'medium') {
        await Promise.all([
            this.playSound(),
            this.vibrate(vibrationStyle)
        ]);
    }

    /**
     * Ronda feedback: double soft vibration with sound
     * Two short soft impacts simulating two hits on the table
     */
    async triggerRonda() {
        debugLog('[SoundVibrationService] Triggering Ronda feedback');

        // First hit
        await this.playHit('light');

        // Pause between hits (configurable via REACT_APP_VIBRATION_RONDA_PAUSE)
        await new Promise(resolve => setTimeout(resolve, RONDA_PAUSE));

        // Second hit
        await this.playHit('light');
    }

    /**
     * Caída feedback: strong short vibration with sound
     * Single heavy impact
     */
    async triggerCaida() {
        debugLog('[SoundVibrationService] Triggering Caída feedback');
        await this.playHit('heavy');
    }

    /**
     * Rondín feedback: triple soft vibration with sound
     * Three short soft impacts
     */
    async triggerRondin() {
        debugLog('[SoundVibrationService] Triggering Rondín feedback');

        // First hit
        await this.playHit('light');

        // Pause between hits (configurable via REACT_APP_VIBRATION_RONDIN_PAUSE)
        await new Promise(resolve => setTimeout(resolve, RONDIN_PAUSE));

        // Second hit
        await this.playHit('light');

        // Pause between hits
        await new Promise(resolve => setTimeout(resolve, RONDIN_PAUSE));

        // Third hit
        await this.playHit('light');
    }

    /**
     * Pasar en Báciga feedback: medium short vibration with sound
     * Single medium impact
     */
    async triggerPasarBaciga() {
        debugLog('[SoundVibrationService] Triggering Pasar Báciga feedback');
        await this.playHit('medium');
    }

    /**
     * Enable or disable sound
     * @param {boolean} enabled
     */
    setSoundEnabled(enabled) {
        this.soundEnabled = enabled;
        localStorage.setItem('soundEnabled', enabled.toString());
        window.dispatchEvent(new CustomEvent('soundEnabledChanged', { detail: enabled }));
        debugLog('[SoundVibrationService] Sound enabled:', enabled);
    }

    /**
     * Enable or disable vibration
     * @param {boolean} enabled
     */
    setVibrationEnabled(enabled) {
        this.vibrationEnabled = enabled;
        localStorage.setItem('vibrationEnabled', enabled.toString());
        window.dispatchEvent(new CustomEvent('vibrationEnabledChanged', { detail: enabled }));
        debugLog('[SoundVibrationService] Vibration enabled:', enabled);
    }

    /**
     * Get current sound enabled state
     * @returns {boolean}
     */
    isSoundEnabled() {
        return this.soundEnabled;
    }

    /**
     * Get current vibration enabled state
     * @returns {boolean}
     */
    isVibrationEnabled() {
        return this.vibrationEnabled;
    }

    /**
     * Check if the service is ready
     * @returns {boolean}
     */
    isReady() {
        return this.initialized;
    }
}

// Export singleton instance
export const soundVibrationService = new SoundVibrationService();
export default soundVibrationService;
